import React, { useRef, useCallback, MouseEvent } from "react";
import { clsx } from "clsx";

interface ReactionButtonProps {
    reaction: string;
    count: number;
    isUserReacted: boolean;
    onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
    onLongPress?: () => void;
    className?: string;
}

const ReactionButton: React.FC<ReactionButtonProps> = ({
    reaction,
    count,
    isUserReacted,
    onClick,
    onLongPress,
    className = "",
}) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isLongPress = useRef(false);

    const handleMouseDown = useCallback(() => {
        isLongPress.current = false;
        timeoutRef.current = setTimeout(() => {
            isLongPress.current = true;
            onLongPress?.();
        }, 500); // 500ms for long press
    }, [onLongPress]);

    const handleMouseUp = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const handleMouseLeave = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const handleClick = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            if (isLongPress.current) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            onClick?.(e);
        },
        [onClick],
    );

    return (
        <button
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleMouseDown}
            onTouchEnd={handleMouseUp}
            onClick={handleClick}
            className={clsx(
                "tw-flex tw-items-center tw-gap-1 tw-px-2 tw-h-[2em] tw-rounded-full tw-transition-all tw-duration-200 tw-text-sm tw-font-medium",
                {
                    "tw-bg-purple-100 tw-text-purple-700": isUserReacted,
                    "tw-bg-gray-100 tw-text-gray-700 hover:tw-bg-gray-200": !isUserReacted,
                },
                className,
            )}
        >
            <span className="tw-text-sm">{reaction}</span>
            {count > 0 && <span className="tw-font-semibold">{count}</span>}
        </button>
    );
};

export default ReactionButton;
